import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Edit2, Trash2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

interface Webhook {
  id: string;
  name: string;
  trigger: string;
  http_method: string;
  url: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const HTTP_METHOD_OPTIONS = [
  { value: "POST", label: "POST" },
  { value: "GET", label: "GET" },
  { value: "PUT", label: "PUT" },
  { value: "DELETE", label: "DELETE" },
  { value: "PATCH", label: "PATCH" },
];

const WebhooksTab: React.FC = () => {
  const { id: assistantId } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [formData, setFormData] = useState({
    name: "",
    trigger: "",
    http_method: "POST",
    url: "",
  });

  useEffect(() => {
    if (assistantId) {
      loadWebhooks();
    }
  }, [assistantId]);

  const loadWebhooks = async () => {
    if (!assistantId || !supabase) return;

    try {
      const { data, error } = await supabase
        .from('account_assistant_webhooks')
        .select('*')
        .eq('assistant_id', assistantId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setWebhooks(data || []);
    } catch (error) {
      console.error('Erro ao carregar webhooks:', error);
      showMessage('error', 'Erro ao carregar webhooks');
    }
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3000);
  };

  const validateUrl = (url: string): boolean => {
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const validateName = (name: string): { valid: boolean; error?: string } => {
    if (!name || name.trim().length === 0) {
      return { valid: false, error: 'Nome é obrigatório' };
    }

    if (name.length < 3) {
      return { valid: false, error: 'Nome deve ter no mínimo 3 caracteres' };
    }

    if (name.length > 50) {
      return { valid: false, error: 'Nome deve ter no máximo 50 caracteres' };
    }

    if (!/^[a-z][a-z0-9_]*$/.test(name)) {
      return { valid: false, error: 'Nome deve começar com letra minúscula e conter apenas letras, números e underscores' };
    }

    return { valid: true };
  };

  const handleAdd = async () => {
    const nameValidation = validateName(formData.name);
    if (!nameValidation.valid) {
      showMessage('error', nameValidation.error || 'Nome inválido');
      return;
    }

    if (!formData.trigger.trim() || !formData.http_method || !formData.url.trim()) {
      showMessage('error', 'Todos os campos são obrigatórios');
      return;
    }

    if (!validateUrl(formData.url)) {
      showMessage('error', 'URL inválida. Use http:// ou https://');
      return;
    }

    if (!assistantId || !user?.account_id || !supabase) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('account_assistant_webhooks')
        .insert({
          account_id: user.account_id,
          assistant_id: assistantId,
          name: formData.name.trim(),
          trigger: formData.trigger.trim(),
          http_method: formData.http_method,
          url: formData.url.trim()
        });

      if (error) throw error;

      showMessage('success', 'Webhook adicionado com sucesso!');
      setFormData({ name: "", trigger: "", http_method: "POST", url: "" });
      await loadWebhooks();
    } catch (error) {
      console.error('Erro ao adicionar webhook:', error);
      showMessage('error', 'Erro ao adicionar webhook');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (webhook: Webhook) => {
    setEditingId(webhook.id);
    setFormData({
      name: webhook.name,
      trigger: webhook.trigger,
      http_method: webhook.http_method,
      url: webhook.url,
    });
  };

  const handleUpdate = async () => {
    if (!editingId || !supabase) return;

    const nameValidation = validateName(formData.name);
    if (!nameValidation.valid) {
      showMessage('error', nameValidation.error || 'Nome inválido');
      return;
    }

    if (!formData.trigger.trim() || !formData.http_method || !formData.url.trim()) {
      showMessage('error', 'Todos os campos são obrigatórios');
      return;
    }

    if (!validateUrl(formData.url)) {
      showMessage('error', 'URL inválida. Use http:// ou https://');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from('account_assistant_webhooks')
        .update({
          name: formData.name.trim(),
          trigger: formData.trigger.trim(),
          http_method: formData.http_method,
          url: formData.url.trim(),
        })
        .eq('id', editingId);

      if (error) throw error;

      showMessage('success', 'Webhook atualizado com sucesso!');
      setEditingId(null);
      setFormData({ name: "", trigger: "", http_method: "POST", url: "" });
      await loadWebhooks();
    } catch (error) {
      console.error('Erro ao atualizar webhook:', error);
      showMessage('error', 'Erro ao atualizar webhook');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Deseja realmente remover este webhook?')) return;
    if (!supabase) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('account_assistant_webhooks')
        .delete()
        .eq('id', id);

      if (error) throw error;

      showMessage('success', 'Webhook removido com sucesso!');
      await loadWebhooks();
    } catch (error) {
      console.error('Erro ao remover webhook:', error);
      showMessage('error', 'Erro ao remover webhook');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setFormData({ name: "", trigger: "", http_method: "POST", url: "" });
  };

  return (
    <div className="w-full flex pb-8 flex-col gap-4">
      {message && (
        <div className={`p-3 rounded-md ${
          message.type === 'success'
            ? 'bg-green-500/20 border border-green-500/30 text-green-300'
            : 'bg-red-500/20 border border-red-500/30 text-red-300'
        }`}>
          {message.text}
        </div>
      )}

      <div className="flex w-full flex-col items-start gap-3 p-4 bg-[#27272A] border border-[#3F3F464D] rounded-lg">
        <h3 className="text-lg font-semibold text-neutral-50 mb-2">
          {editingId ? 'Editar Webhook' : 'Novo Webhook'}
        </h3>

        <div className="w-full flex flex-col gap-3">
          <div className="flex w-full flex-col items-start gap-2">
            <Label htmlFor="name">Nome do Webhook</Label>
            <Input
              placeholder="Ex: send_email, create_ticket, notify_team"
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value.toLowerCase() }))}
              className="bg-neutral-600 !w-full text-[14px] placeholder:text-[#71717A] text-neutral-50 !h-[44px] border focus:ring-white border-[#3F3F46] font-mono"
            />
            <p className="text-xs text-neutral-400">
              Apenas letras minúsculas, números e underscores. Deve começar com letra (3-50 caracteres)
            </p>
          </div>

          <div className="flex w-full flex-col items-start gap-2">
            <Label htmlFor="trigger">Quando executar</Label>
            <Input
              placeholder="Ex: ao receber mensagem, ao enviar resposta, etc."
              id="trigger"
              value={formData.trigger}
              onChange={(e) => setFormData(prev => ({ ...prev, trigger: e.target.value }))}
              className="bg-neutral-600 !w-full text-[14px] placeholder:text-[#71717A] text-neutral-50 !h-[44px] border focus:ring-white border-[#3F3F46]"
            />
          </div>

          <div className="flex w-full flex-col items-start gap-2">
            <Label htmlFor="http_method">Método HTTP</Label>
            <Select key="http-method-select" value={formData.http_method} onValueChange={(value) => setFormData(prev => ({ ...prev, http_method: value }))}>
              <SelectTrigger className="w-full !h-[44px] rounded-[12px] bg-neutral-600">
                <SelectValue placeholder="Selecione o método" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {HTTP_METHOD_OPTIONS.map((option) => (
                    <SelectItem key={`http-method-${option.value}`} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="flex w-full flex-col items-start gap-2">
            <Label htmlFor="url">URL do Webhook</Label>
            <Input
              placeholder="https://exemplo.com/webhook"
              id="url"
              value={formData.url}
              onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
              className="bg-neutral-600 !w-full text-[14px] placeholder:text-[#71717A] text-neutral-50 !h-[44px] border focus:ring-white border-[#3F3F46]"
              type="url"
            />
          </div>
        </div>

        <div className="flex gap-2 mt-2">
          {editingId ? (
            <>
              <Button
                onClick={handleUpdate}
                disabled={loading}
                className="flex items-center gap-2"
              >
                {loading ? 'Atualizando...' : 'Atualizar'}
              </Button>
              <Button
                onClick={handleCancelEdit}
                disabled={loading}
                variant="outline"
                className="flex items-center gap-2"
              >
                Cancelar
              </Button>
            </>
          ) : (
            <Button
              onClick={handleAdd}
              disabled={loading}
              className="flex items-center gap-2"
            >
              <Plus size={16} />
              {loading ? 'Adicionando...' : 'Adicionar'}
            </Button>
          )}
        </div>
      </div>

      {webhooks.length > 0 ? (
        <div className="w-full flex flex-col gap-3">
          <h3 className="text-lg font-semibold text-neutral-50">Webhooks Cadastrados</h3>
          <div className="overflow-x-auto">
            <table className="w-full border border-[#3F3F464D] rounded-lg">
              <thead className="bg-[#27272A]">
                <tr>
                  <th className="text-left p-3 text-neutral-50 font-semibold">Nome</th>
                  <th className="text-left p-3 text-neutral-50 font-semibold">Trigger</th>
                  <th className="text-left p-3 text-neutral-50 font-semibold">Método</th>
                  <th className="text-left p-3 text-neutral-50 font-semibold">URL</th>
                  <th className="text-center p-3 text-neutral-50 font-semibold w-[120px]">Ações</th>
                </tr>
              </thead>
              <tbody>
                {webhooks.map((webhook) => (
                  <tr key={webhook.id} className="border-t border-[#3F3F464D] hover:bg-[#27272A]/50">
                    <td className="p-3 text-neutral-50 font-mono text-sm">{webhook.name}</td>
                    <td className="p-3 text-neutral-50">{webhook.trigger}</td>
                    <td className="p-3 text-neutral-50">
                      <span className="px-2 py-1 bg-neutral-600 rounded text-xs">
                        {webhook.http_method}
                      </span>
                    </td>
                    <td className="p-3 text-neutral-50 break-all max-w-[300px]">{webhook.url}</td>
                    <td className="p-3">
                      <div className="flex justify-center items-center gap-2">
                        <button
                          onClick={() => handleEdit(webhook)}
                          disabled={loading}
                          className="p-2 hover:bg-neutral-600 rounded disabled:opacity-50 transition-colors"
                          title="Editar"
                        >
                          <Edit2 size={16} className="text-neutral-50" />
                        </button>
                        <button
                          onClick={() => handleDelete(webhook.id)}
                          disabled={loading}
                          className="p-2 hover:bg-red-500/20 rounded disabled:opacity-50 transition-colors"
                          title="Excluir"
                        >
                          <Trash2 size={16} className="text-red-400" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="text-center py-8 text-neutral-400 bg-[#27272A]/30 rounded-lg border border-[#3F3F464D]">
          <p>Nenhum webhook cadastrado ainda</p>
          <p className="text-sm mt-2">Adicione webhooks para integrar com sistemas externos</p>
        </div>
      )}
    </div>
  );
};

export default WebhooksTab;
